import type { BimChapterEnum, BimPriceLevelEnum, BimWorkOrderChangeObjectData } from 'gql/graphql';
import type { IListItem } from 'types/Contract';
import { destroyAgGridTableFilters } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { useState, useMemo, useCallback, useEffect } from 'react';

import { useBimWorkOrderChangeTotals } from '../../hooks';

interface ICorrectUpdateWorkOrderChangeData {
    workOrderChangeRootObject: IListItem<number> | undefined;
    workOrderChangeObjectsData: BimWorkOrderChangeObjectData[] | undefined;
    refreshWorkOrderChangeChapterData: () => void;
    refreshWorkOrderChangeObjects: () => void;
    currentTableApi: AgGridReact | null;
    workOrderChangeRootObjectSetter: (rootObject: IListItem<number> | undefined) => void;
    workOrderChangeId: number | undefined;
    constructionTypeId: number | undefined;
    bimChapter: BimChapterEnum | undefined;
    otherChapter: BimChapterEnum | undefined;
    priceLevel: BimPriceLevelEnum | undefined;
}

const priceTableFilters = ['object.name', 'planValue.code', 'planValue.name', 'rationale'];

export const useCorrectUpdateWorkOrderChangeData = ({
    workOrderChangeRootObject,
    workOrderChangeObjectsData,
    refreshWorkOrderChangeChapterData,
    refreshWorkOrderChangeObjects,
    currentTableApi,
    workOrderChangeRootObjectSetter,
    workOrderChangeId,
    bimChapter,
    constructionTypeId,
    priceLevel,
}: ICorrectUpdateWorkOrderChangeData) => {
    const isPriceTable = useMemo(() => !!bimChapter, [bimChapter]);

    const { makeRefreshTotalsData } = useBimWorkOrderChangeTotals({
        workOrderChangeId,
        chapter: bimChapter,
        workOrderObjectId: workOrderChangeRootObject?.workOrderObjectId,
        workOrderChangeObjectId: workOrderChangeRootObject?.workOrderChangeObjectId,
        constructionTypeId,
        withEffect: false,
        priceLevel,
    });

    const [isUpdateLegal, setIsUpdateLegal] = useState(false);

    const needUpdateObjects = useMemo(
        () =>
            !!(
                workOrderChangeRootObject &&
                !workOrderChangeRootObject?.workOrderChangeObjectId &&
                workOrderChangeRootObject?.workOrderObjectId
            ),
        [workOrderChangeRootObject],
    );

    const resetTableFilters = useCallback(() => {
        if (currentTableApi) {
            if (isPriceTable) {
                destroyAgGridTableFilters({
                    api: currentTableApi.api,
                    columns: priceTableFilters,
                });
            }
        }
    }, [currentTableApi, isPriceTable]);

    const updateCurrentTableWithFilter = useCallback(() => {
        if (currentTableApi) {
            if (isPriceTable) {
                refreshWorkOrderChangeChapterData();
                makeRefreshTotalsData();
                resetTableFilters();
            }

            currentTableApi.api.refreshInfiniteCache();
        }
    }, [currentTableApi, isPriceTable, makeRefreshTotalsData, refreshWorkOrderChangeChapterData, resetTableFilters]);

    const makeRefreshCurrentTable = useCallback(() => {
        if (needUpdateObjects) {
            refreshWorkOrderChangeObjects();
            setIsUpdateLegal(true);
            resetTableFilters();
        } else {
            updateCurrentTableWithFilter();
        }
    }, [needUpdateObjects, refreshWorkOrderChangeObjects, resetTableFilters, updateCurrentTableWithFilter]);

    useEffect(() => {
        if (needUpdateObjects && isUpdateLegal) {
            const newObject = workOrderChangeObjectsData?.find(
                (object) => object.workOrderObjectId === workOrderChangeRootObject?.workOrderObjectId,
            );

            if (
                newObject?.id &&
                newObject?.workOrderChangeObjectId &&
                workOrderChangeRootObject?.workOrderChangeObjectId !== newObject?.workOrderChangeObjectId
            ) {
                workOrderChangeRootObjectSetter({
                    id: newObject?.id,
                    label: newObject?.name,
                    workOrderObjectId: newObject?.workOrderObjectId,
                    workOrderChangeObjectId: newObject?.workOrderChangeObjectId,
                });

                setIsUpdateLegal(false);
            }
        }
    }, [
        isUpdateLegal,
        needUpdateObjects,
        workOrderChangeObjectsData,
        workOrderChangeRootObject,
        workOrderChangeRootObjectSetter,
    ]);

    return { makeRefreshCurrentTable };
};
